/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { soundingsActions } from '../../store/reducer';
import { getPanelIsOpen, getPanelCustomWidth } from '../../store/selectors';
import type { SoundingsModuleState } from '../../store/types';
import { SoundingsActionOrigin } from '../../store/types';
import { ResizablePanel } from '../ResizablePanel/ResizablePanel';
import type { ResizablePanelWrapperProps } from '../ResizablePanel/ResizablePanelStandalone';

export const SoundingsPanelConnect = React.forwardRef<
  HTMLDivElement,
  ResizablePanelWrapperProps
>(
  (
    {
      panelId,
      viewId,
      name,
      children,
      headerAction,
      isDisplayed,
      maxWidth,
      minWidth,
      handleButtonComponent,
      onOpenChange,
      onResizeStart,
      onResizeEnd,
    },
    ref,
  ): React.ReactElement => {
    if (!panelId || !viewId) {
      throw new Error(
        'SoundingsPanelConnect requires both `panelId` and `viewId` props. ' +
          'Use it inside a SortablePanels that has `viewId` set, or pass both directly.',
      );
    }
    const dispatch = useDispatch();

    const isOpen = useSelector((store: SoundingsModuleState) =>
      getPanelIsOpen(store, viewId, panelId),
    );
    const customWidth = useSelector((store: SoundingsModuleState) =>
      getPanelCustomWidth(store, viewId, panelId),
    );
    const handleSetIsOpen = React.useCallback(
      (open: boolean) => {
        dispatch(
          soundingsActions.setPanelIsOpen({
            panelId,
            viewId,
            isOpen: open,
            origin: SoundingsActionOrigin.soundingsComponent,
          }),
        );
        onOpenChange?.(open);
      },
      [dispatch, viewId, panelId, onOpenChange],
    );

    const handleSetCustomWidth = React.useCallback(
      (width: number) => {
        dispatch(
          soundingsActions.setPanelCustomWidth({
            panelId,
            viewId,
            customWidth: width,
            origin: SoundingsActionOrigin.soundingsComponent,
          }),
        );
      },
      [dispatch, viewId, panelId],
    );

    return (
      <ResizablePanel
        customWidth={customWidth}
        handleButtonComponent={handleButtonComponent}
        headerAction={headerAction}
        isDisplayed={isDisplayed}
        isOpen={isOpen}
        maxWidth={maxWidth}
        minWidth={minWidth}
        name={name}
        onResizeEnd={onResizeEnd}
        onResizeStart={onResizeStart}
        onSetCustomWidth={handleSetCustomWidth}
        onSetIsOpen={handleSetIsOpen}
        ref={ref}
      >
        {children}
      </ResizablePanel>
    );
  },
);

SoundingsPanelConnect.displayName = 'SoundingsPanelConnect';
